import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import {
  IconLayoutDashboard,
  IconUsersGroup,
  IconClipboardCheck,
  IconReportAnalytics,
  IconChartHistogram,
  IconChartArea,
  IconChecklist,
  IconFileText,
  IconShieldCheck,
  IconHistory,
  IconAdjustments,
  IconBuildingSkyscraper,
  IconUsers,
  IconRobot,
  IconBellRinging,
  IconCoin,
} from '@tabler/icons-react';
// Sprint42:删 IconStar/IconCoin(/portal 与 /investment 占位项已下线)
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { useBranding } from '@/contexts/BrandingContext';
import { canSee } from '@/lib/role';
import type { Role } from '@/lib/role';

const _PLATFORM_DEFAULT_NAMES = new Set([
  '__default__',
  '锚点科技 · 孵化器一体化运营平台',
]);

interface MenuDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** 占位（M3-β/ε 后启用）— 渲染为灰色不可点 */
  placeholder?: boolean;
}

// Sprint23-UX:菜单顺序按用户业务流重排 — 看板优先 → 配置 → 数据 → 管理 → 系统
const ALL_MENUS: MenuDef[] = [
  // === 0. 我的待办 — 企服/管理者每日打开第一眼(Sprint 32) ===
  { key: '/my-todos',              label: '我的待办',         icon: <IconBellRinging size={16} /> },
  // Sprint57-D / Phase D:任务系统 WY 今日任务 — 暂隐藏(等 SFL UAT)
  // { key: '/tasks/my-todos',        label: '✅ 我的任务',      icon: <IconChecklist size={16} /> },
  // === 1. 入口看板 ===
  { key: '/dashboard',             label: '仪表盘',           icon: <IconLayoutDashboard size={16} /> },
  // Sprint57-D / Phase D:JXY 管理者驾驶舱 — 暂隐藏(等 SFL UAT)
  // { key: '/dashboard/jxy',         label: '🎯 管理者驾驶舱',  icon: <IconChartHistogram size={16} /> },
  // Sprint57-D / Phase D:任务中心 — 暂隐藏(等 SFL UAT)
  // { key: '/tasks',                 label: '📋 任务中心',      icon: <IconClipboardCheck size={16} /> },
  // Sprint 20-extra: "达标看板" → "孵化器达标看板",并入原 "就绪检查"
  { key: '/compliance',            label: '孵化器达标看板',   icon: <IconChartHistogram size={16} /> },
  // Sprint 20-extra:企业资质申报看板(管理者视角 — 表格/进度/导出)
  { key: '/declaration-dashboard', label: '企业资质申报看板', icon: <IconReportAnalytics size={16} /> },

  // === 2. 政策配置 ===
  { key: '/policy-packs',          label: '政策包配置',       icon: <IconAdjustments size={16} /> },
  // Sprint 20 S20-B:孵化器档案录入入口(tenant_admin / platform_operator)
  // Sprint23-Backlog:有些字段应来自系统自动汇集(待 Sprint 24)
  { key: '/incubator/profile',     label: '孵化器档案',       icon: <IconBuildingSkyscraper size={16} /> },

  // === 3. 业务数据 ===
  { key: '/subjects',              label: '主体管理',         icon: <IconUsersGroup size={16} /> },
  // Sprint57-C:报表中心(政府数据交付中枢)— 置于主体后/申报前,突出 ADR-001 第 3 层
  { key: '/reports',               label: '📊 报表中心',      icon: <IconReportAnalytics size={16} /> },
  { key: '/cases',                 label: '申报案例',         icon: <IconClipboardCheck size={16} /> },
  // Sprint 31-I:淘汰企业台账 — 功能被主体管理(在孵企业 Tab)+ 年度指标 Tab + monitoring overlay 替代
  // { key: '/ledger',                label: '企业台账',         icon: <IconTable size={16} /> },
  { key: '/materials',             label: '材料台账',         icon: <IconFileText size={16} /> },
  // Sprint 52 Phase 1C:应收账单看板
  { key: '/receivables',           label: '💰 应收账单',      icon: <IconCoin size={16} /> },
  { key: '/monitoring',            label: '监控对比',         icon: <IconChartArea size={16} /> },

  // === 4. 系统管理 ===
  // Sprint41:删「企服工作台」/cockpit 入口 — 真业务由 Sprint 32 「我的待办」覆盖且更精
  { key: '/_admin/users',          label: '用户管理',         icon: <IconUsers size={16} /> },
  { key: '/tenants/theme',         label: '白标主题',         icon: <IconShieldCheck size={16} /> },
  { key: '/audit-logs',            label: '审计日志',         icon: <IconHistory size={16} /> },

  // === 6. 平台运营(platform_operator 专属;canSee 守可见性) ===
  // R3-Sprint10:用户/租户生命周期 UI(v5 §13)
  { key: '/_admin/tenants',        label: '租户管理',         icon: <IconBuildingSkyscraper size={16} /> },
  // Sprint 16:资质目录
  { key: '/_admin/qualifications', label: '资质目录',         icon: <IconChecklist size={16} /> },
  // R3-Sprint13 Phase 2:LLM 提供方配置 + 调用历史
  { key: '/_admin/llm-settings',   label: 'LLM 设置',         icon: <IconRobot size={16} /> },

  // Sprint42:删 /portal 灰色 placeholder — enterprise 登录直接跳 /me/enterprise(LoginPage:74),
  // /portal 路径从未对应任何路由,留着是历史 dead UI。/investment 同样删 — investor 走 /me/investor。
];

function buildMenuItems(role: Role | null): MenuProps['items'] {
  return ALL_MENUS.filter((m) => canSee(m.key, role)).map((m) => ({
    key: m.key,
    icon: m.icon,
    label: m.placeholder ? (
      <span
        title="占位 — 后续 sprint 解锁"
        style={{ opacity: 0.55 }}
      >
        {m.label}
      </span>
    ) : (
      m.label
    ),
    disabled: m.placeholder,
  }));
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useAuth();
  const { tenant } = useTenant();
  const { branding } = useBranding();

  // 平台运营方(platform_operator)= 平台方(AnchorTech)本身,不套任何租户白标 —
  // 始终显示平台自身品牌「孵化器一体化运营平台」,不显示某租户(如圣丰联智谷)的白标 logo/名称。
  const isPlatform = role === 'platform_operator';

  // R3-Sprint12-A UX:租户白标(已 init tenant_branding)优先;否则用 builtin theme 兜底
  //   platform_operator 强制走平台品牌分支(hasTenantBranding 恒 false)
  const hasTenantBranding =
    !isPlatform &&
    !_PLATFORM_DEFAULT_NAMES.has(branding.tenant_slug ?? '') &&
    !_PLATFORM_DEFAULT_NAMES.has(branding.company_name ?? '');
  const displayName = isPlatform
    ? '孵化器一体化运营平台'
    : hasTenantBranding
      ? branding.company_name ?? tenant.displayName
      : tenant.displayName;
  const tagline = isPlatform
    ? '锚点科技 AnchorTech'
    : hasTenantBranding
      ? branding.tagline ?? tenant.tagline
      : tenant.tagline;
  const logoUrl =
    !isPlatform && hasTenantBranding && branding.logo_url && /^https?:\/\//i.test(branding.logo_url)
      ? branding.logo_url
      : null;

  const items = buildMenuItems(role);

  return (
    <div
      style={{
        width: 220,
        background: 'var(--color-brand-navy)',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '18px 16px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={displayName}
            width={36}
            height={36}
            style={{ borderRadius: '50%', flex: 'none', objectFit: 'contain', background: '#fff' }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: 32,
              height: 36,
              clipPath: 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)',
              background: `linear-gradient(135deg, var(--color-brand-cyan), ${branding.primary_color ?? tenant.colorPrimary})`,
              flex: 'none',
            }}
          />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={displayName}
          >
            {displayName}
          </div>
          <div
            style={{
              color: 'var(--color-text-on-dark-hint)',
              fontSize: 10,
              letterSpacing: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={tagline}
          >
            {tagline}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          style={{ background: 'transparent', borderRight: 'none' }}
          items={items}
          onClick={({ key }) => navigate(key)}
        />
      </div>
      {/* R3-Sprint12-A-UX7:左下 footer 显示 ICP 备案号(填了才显示,不占位) */}
      {branding.icp_filing && (
        <div
          style={{
            padding: '10px 16px',
            fontSize: 11,
            color: 'var(--color-text-on-dark-hint)',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            textAlign: 'center',
          }}
          title={branding.icp_filing}
        >
          {branding.icp_filing}
        </div>
      )}
    </div>
  );
}
